//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import { FlatDefaultIconButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { BodyBoldText, BodyText } from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { Product, ProductType } from '@models/IItemMetadata';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { isDefined } from '@utils/filterPredicates';

const CRAWL_TYPE: ProductType[] = ['Crawl', 'UpdateCrawl'];

export interface ProductTagItemProps {
  isDisabled: boolean;
  isCatalogConnected: boolean;
  onEditProductTag: (productTag: Product) => void;
  onRemoveProductTag: (productTag: Product) => void;
  productTag: Product;
}

export const ProductTagItem = ({
  isCatalogConnected,
  isDisabled,
  onEditProductTag,
  onRemoveProductTag,
  productTag,
}: ProductTagItemProps) => {
  const { imageUrl, name, position, productType, spatialTag } = productTag;

  return (
    <ProductTag>
      {isDefined(imageUrl) && imageUrl.length > 0 ? (
        <>
          {isDefined(spatialTag) && isDefined(position) && (
            <BodyText>{position}</BodyText>
          )}
          <ProductImage alt={name ?? 'Product image'} src={imageUrl} />
        </>
      ) : (
        <ProductImage alt={name ?? 'Product image'} src={ProductImage} />
      )}
      <Title>{name ?? 'Unnamed Product'}</Title>
      <Actions>
        {!isDisabled && (
          <>
            <FlatDefaultIconButton
              aria-label='Edit product'
              iconName='edit'
              onClick={() => onEditProductTag(productTag)}
              trackingId={UIATrackingIds.PRODUCTS_PRODUCT_TAGS_EDIT}
            />
            <FlatDefaultIconButton
              aria-label='Delete product'
              iconName='delete'
              onClick={() => onRemoveProductTag(productTag)}
              trackingId={UIATrackingIds.PRODUCTS_PRODUCT_TAGS_REMOVE}
            />
          </>
        )}
      </Actions>
      {isCatalogConnected && (
        <>
          {isDefined(productType) && CRAWL_TYPE.includes(productType) && (
            <SmallTooltip
              content="This tag was added via URL (not from your catalog), which can impact your consumer's experiences."
              target={<Icon name='link' />}
            />
          )}
          <BodyBoldText>
            {(() => {
              if (productTag.isExpired) {
                return 'Removed from catalog';
              } else if (
                productTag.isAvailable &&
                isDefined(productTag.price)
              ) {
                return productTag.price;
              } else {
                return 'Out of stock';
              }
            })()}
          </BodyBoldText>
        </>
      )}
    </ProductTag>
  );
};

const ProductTag = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: start;
    border: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    border-radius: ${theme.borderRadii[2]};
  `,
);

const Title = styled(BodyText)(
  ({ theme }) => `
    padding: ${theme.borderRadii[4]};
  `,
);

const Actions = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: end;
    align-items: start;
    padding: ${theme.borderRadii[3]};
  `,
);

const ProductImage = styled(ImageWithErrorHandling)(
  ({ theme }) => css`
    width: calc(${theme.spacings[7]} + ${theme.spacings[5]});
    aspect-ratio: 1;
    object-fit: cover;
  `,
);
